import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { INSTITUTION_KEYS } from '@/shared/constants/institutions';
import adminService from '@/shared/services/adminService';
import { serverMessage } from '@/shared/utils/serverMessage';
import CategoryChart from './reports/CategoryChart';
import CountTables from './reports/CountTables';
import FlowMatrix from './reports/FlowMatrix';
import Headline from './reports/Headline';
import InstitutionChart from './reports/InstitutionChart';
import MonthlyTrendChart from './reports/MonthlyTrendChart';
import RangeControl from './reports/RangeControl';
import { isBackwards, rangeFor } from './reports/dateRange';
import { saveBlob } from './reports/download';
import './reports/Reports.css';

const LOAD_ERROR =
    'Could not load the reports. Check your connection and try again.';

/** Listings a member could request today: available, or already requested. */
function activeNow(listings) {
    return INSTITUTION_KEYS.reduce(
        (sum, key) =>
            sum +
            listings.byInstitution[key].available +
            listings.byInstitution[key].requested,
        0
    );
}

function ReportsTab() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadError, setDownloadError] = useState('');
    const [preset, setPreset] = useState('all');
    const [range, setRange] = useState(rangeFor('all'));
    // The answer to the most recent request is the only one kept, so a slow
    // response for an earlier range cannot overwrite a newer one.
    const latest = useRef(0);

    const backwards = isBackwards(range);

    useEffect(() => {
        if (backwards) {
            return;
        }
        const request = ++latest.current;
        async function load() {
            try {
                const next = await adminService.reportSummary(range);
                if (request === latest.current) {
                    setSummary(next);
                    setError('');
                }
            } catch (_error) {
                if (request === latest.current) {
                    setError(LOAD_ERROR);
                }
            } finally {
                if (request === latest.current) {
                    setLoading(false);
                }
            }
        }
        load();
        // `range` is read through its two fields so a new object with the
        // same days does not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.from, range.to, backwards]);

    const choosePreset = (key) => {
        setPreset(key);
        setRange(rangeFor(key));
    };

    const chooseDays = (next) => {
        setPreset('custom');
        setRange(next);
    };

    const download = (fetchFile, failure) => async () => {
        setDownloadError('');
        try {
            const { blob, filename } = await fetchFile();
            saveBlob(blob, filename);
        } catch (caught) {
            setDownloadError(serverMessage(caught, failure));
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading reports" />;
    }

    if (error && !summary) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const { listings, exchanges, requests, members, impact } = summary;
    const empty = impact.totals.transfers === 0;
    const months = impact.byMonth;

    return (
        <>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h2 className="h4 mb-0">Reports</h2>
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        onClick={download(
                            () => adminService.exportCsv(),
                            'Could not download the export. Try again.'
                        )}
                    >
                        Download completed exchanges CSV
                    </Button>
                    <Button
                        onClick={download(
                            () => adminService.impactCsv(range),
                            'Could not download the impact file. Try again.'
                        )}
                        disabled={backwards}
                    >
                        Download impact CSV
                    </Button>
                </div>
            </div>
            <p className="text-muted small">
                <time dateTime={summary.generatedAt}>
                    Generated {new Date(summary.generatedAt).toLocaleString()}
                </time>
            </p>
            <p className="text-muted">
                Nobody is named in a report. Pounds and dollars are the
                estimates posters entered per item, multiplied by the quantity
                posted and rounded to the whole unit, and count transferred
                exchanges only.
            </p>
            {downloadError && <Alert variant="danger">{downloadError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            <RangeControl
                range={range}
                preset={preset}
                onPreset={choosePreset}
                onChange={chooseDays}
            />
            <Headline totals={impact.totals} activeNow={activeNow(listings)} />
            {empty ? (
                <Alert variant="info">
                    No transfers were completed in this range, so there is
                    nothing to chart. Choose a wider range or All time.
                </Alert>
            ) : (
                <>
                    <MonthlyTrendChart months={months} />
                    <InstitutionChart
                        giving={impact.byGivingInstitution}
                        receiving={impact.byReceivingInstitution}
                    />
                    <CategoryChart byCategory={impact.byCategory} />
                    <FlowMatrix flow={impact.flow} />
                </>
            )}
            <h3 className="h5 mt-4">Counts, all time</h3>
            <CountTables
                listings={listings}
                exchanges={exchanges}
                requests={requests}
                members={members}
            />
        </>
    );
}

export default ReportsTab;
